/**
 * ECOSMITH Pakistan - Public Site Main Entry
 * Initializes Lenis, GSAP, and Web Components
 */

import './styles/main.css';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// ==================== LENIS SMOOTH SCROLL ====================

let lenis;

function initLenis() {
  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    console.log('[ECOSMITH] Reduced motion preferred - Lenis disabled');
    return null;
  }

  lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    orientation: 'vertical',
    gestureOrientation: 'vertical',
    smoothWheel: true,
    wheelMultiplier: 1,
    touchMultiplier: 2,
  });

  // Integrate Lenis with GSAP ScrollTrigger
  lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });

  gsap.ticker.lagSmoothing(0);

  return lenis;
}

export function getLenis() {
  return lenis;
}

export function scrollTo(target, options = {}) {
  if (lenis) {
    lenis.scrollTo(target, options);
  } else {
    // Fallback for reduced motion
    const element = typeof target === 'string' ? document.querySelector(target) : target;
    if (element) {
      element.scrollIntoView({ behavior: 'auto', ...options });
    }
  }
}

// ==================== VIDEO LAZY LOADING ====================

class VideoLazyLoader {
  constructor() {
    this.videos = new Map();
    this.observer = null;
    this.init();
  }

  init() {
    // Check for reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // Replace videos with poster images
      document.querySelectorAll('video[data-lazy]').forEach(video => {
        const poster = video.getAttribute('poster');
        if (poster) {
          const img = document.createElement('img');
          img.src = poster;
          img.alt = video.getAttribute('aria-label') || 'Video content';
          img.className = video.className;
          img.style.objectFit = 'cover';
          video.parentNode.replaceChild(img, video);
        }
      });
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    document.querySelectorAll('video[data-lazy]').forEach(video => {
      this.videos.set(video, { loaded: false, playing: false });
      this.observer.observe(video);
    });
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      const video = entry.target;
      const state = this.videos.get(video);

      if (entry.isIntersecting) {
        this.loadAndPlay(video, state);
      } else {
        this.pause(video, state);
      }
    });
  }

  async loadAndPlay(video, state) {
    if (!state.loaded) {
      video.preload = 'auto';
      
      // Wait for readyState
      if (video.readyState < 3) {
        await new Promise((resolve) => {
          const onCanPlay = () => {
            video.removeEventListener('canplaythrough', onCanPlay);
            resolve();
          };
          video.addEventListener('canplaythrough', onCanPlay);
          video.load();
        });
      }
      
      state.loaded = true;
    }

    try {
      video.muted = true;
      video.loop = true;
      await video.play();
      state.playing = true;
    } catch (error) {
      console.warn('[VideoLazyLoader] Autoplay prevented:', error);
    }
  }

  pause(video, state) {
    if (state.playing) {
      video.pause();
      video.preload = 'none';
      state.playing = false;
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.videos.clear();
  }
}

// ==================== SCROLL VIDEO CONTROLLER ====================

export class ScrollVideoController {
  constructor(videoElement, options = {}) {
    this.video = videoElement;
    this.options = {
      start: 'top center',
      end: 'bottom center',
      scrub: 1,
      pin: false,
      anticipatePin: 1,
      ...options
    };
    
    this.scrollTrigger = null;
    this.isReady = false;
    this.fallbackMode = false;
    
    this.init();
  }

  async init() {
    // Check for reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      this.enableFallback();
      return;
    }

    // Wait for video metadata
    if (this.video.readyState < 1) {
      await new Promise((resolve) => {
        this.video.addEventListener('loadedmetadata', resolve, { once: true });
      });
    }

    // Check if video seeking works
    const canSeek = await this.testSeeking();
    
    if (canSeek) {
      this.setupScrollTrigger();
    } else {
      this.enableFallback();
    }
  }

  async testSeeking() {
    return new Promise((resolve) => {
      const testTime = this.video.duration * 0.5;
      let seekSuccessful = false;

      const onSeeked = () => {
        seekSuccessful = true;
        this.video.removeEventListener('seeked', onSeeked);
        resolve(true);
      };

      const onError = () => {
        this.video.removeEventListener('error', onError);
        resolve(false);
      };

      this.video.addEventListener('seeked', onSeeked);
      this.video.addEventListener('error', onError);

      // Set a timeout in case seeking hangs
      setTimeout(() => {
        if (!seekSuccessful) {
          this.video.removeEventListener('seeked', onSeeked);
          resolve(false);
        }
      }, 1000);

      try {
        this.video.currentTime = testTime;
      } catch (e) {
        resolve(false);
      }
    });
  }

  setupScrollTrigger() {
    const duration = this.video.duration;
    
    this.scrollTrigger = ScrollTrigger.create({
      trigger: this.video.parentElement,
      start: this.options.start,
      end: this.options.end,
      scrub: this.options.scrub,
      pin: this.options.pin,
      anticipatePin: this.options.anticipatePin,
      onUpdate: (self) => {
        const targetTime = duration * self.progress;
        
        // Only seek if significantly different to avoid jitter
        if (Math.abs(this.video.currentTime - targetTime) > 0.1) {
          this.video.currentTime = targetTime;
        }
      }
    });

    this.isReady = true;
  }

  enableFallback() {
    this.fallbackMode = true;
    
    // Show poster or first frame instead
    const poster = this.video.getAttribute('poster');
    if (poster) {
      const img = document.createElement('img');
      img.src = poster;
      img.alt = 'Video frame';
      img.className = this.video.className;
      img.style.objectFit = 'cover';
      this.video.parentNode.replaceChild(img, this.video);
    }
    
    console.log('[ScrollVideoController] Fallback mode enabled');
  }

  destroy() {
    if (this.scrollTrigger) {
      this.scrollTrigger.kill();
    }
  }
}

// ==================== NAVBAR SCROLL BEHAVIOR ====================

function initNavbar() {
  const navbar = document.querySelector('eco-nav');
  if (!navbar) return;

  let lastScrollY = 0;
  let ticking = false;

  const updateNavbar = () => {
    const scrollY = window.scrollY;
    
    if (scrollY > 50) {
      navbar.classList.add('scrolled');
    } else {
      navbar.classList.remove('scrolled');
    }

    lastScrollY = scrollY;
    ticking = false;
  };

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(updateNavbar);
      ticking = true;
    }
  }, { passive: true });
}

// ==================== INITIALIZATION ====================

function init() {
  console.log('[ECOSMITH] Initializing public site...');

  // Initialize Lenis smooth scroll
  initLenis();

  // Initialize video lazy loading
  const videoLoader = new VideoLazyLoader();

  // Initialize navbar
  initNavbar();

  // Refresh ScrollTrigger after all components mount
  setTimeout(() => {
    ScrollTrigger.refresh();
  }, 100);

  // Handle resize
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 250);
  }, { passive: true });

  console.log('[ECOSMITH] Site initialized successfully');
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for component use
export { initLenis, VideoLazyLoader };